import React from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface AgentStatus {
  id: number
  username: string
  slug: string
  agent_type: string
  status: 'active' | 'idle' | 'offline'
  last_seen_at: string | null
  current_task: { id: number; summary: string } | null
  last_session: { id: number; status: string; started_at: string } | null
}

interface TaskMetric {
  board_id: number
  board_name: string
  board_slug: string
  counts: {
    pending: number
    in_progress: number
    in_review: number
    completed: number
    accepted: number
    rejected: number
    total: number
  }
}

interface ActivityItem {
  id: number
  event_type: string
  agent: { id: number; username: string; slug: string }
  trackable_type: string
  trackable_id: number
  metadata: Record<string, unknown>
  project_id: number | null
  created_at: string
}

interface Props {
  agent_statuses: AgentStatus[]
  task_metrics: TaskMetric[]
  recent_activities: ActivityItem[]
}

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  idle: 'warning',
  offline: 'default',
}

const EVENT_LABELS: Record<string, string> = {
  task_created: 'Task Created',
  task_status_changed: 'Task Status Changed',
  task_assigned: 'Task Assigned',
  clarification_created: 'Clarification Created',
  clarification_resolved: 'Clarification Resolved',
  review_item_created: 'Review Item Created',
  review_item_resolved: 'Review Item Resolved',
  channel_message_sent: 'Channel Message',
  webhook_event_received: 'Webhook Received',
  webhook_event_processed: 'Webhook Processed',
  session_started: 'Session Started',
  session_completed: 'Session Completed',
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

function getActivityDescription(activity: ActivityItem): string {
  const meta = activity.metadata as Record<string, string>
  switch (activity.event_type) {
    case 'task_created':
      return meta.summary || 'Created a task'
    case 'task_status_changed':
      return `${meta.summary || 'Task'}: ${meta.old_status} → ${meta.new_status}`
    case 'task_assigned':
      return meta.summary || 'Assigned a task'
    case 'clarification_created':
      return `On: ${meta.task_summary || 'task'}`
    case 'review_item_created':
      return `On: ${meta.task_summary || 'task'}`
    case 'channel_message_sent':
      return `In: ${meta.channel_name || 'channel'}`
    case 'webhook_event_received':
    case 'webhook_event_processed':
      return meta.webhook_name || 'Webhook event'
    case 'session_started':
    case 'session_completed':
      return meta.model || 'Agent session'
    default:
      return activity.event_type
  }
}

export default function Show({ agent_statuses, task_metrics, recent_activities }: Props) {
  return (
    <SidebarLayout section="monitoring" title="Dashboard">
      {/* Agent Status Overview */}
      <Typography variant="h6" sx={{ mb: 2 }}>Agent Status</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {agent_statuses.map((agent) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={agent.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    @{agent.username}
                  </Typography>
                  <Chip
                    label={agent.status}
                    size="small"
                    color={STATUS_COLORS[agent.status]}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {agent.agent_type === 'machine' ? 'AI Agent' : 'Human'}
                </Typography>
                {agent.current_task && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                    Working on: {agent.current_task.summary}
                  </Typography>
                )}
                {agent.last_seen_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Last seen: {formatTimeAgo(agent.last_seen_at)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {agent_statuses.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">No agents found.</Typography>
          </Grid>
        )}
      </Grid>

      {/* Task Pipeline Metrics */}
      <Typography variant="h6" sx={{ mb: 2 }}>Task Pipeline</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 4, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>Board</TableCell>
              <TableCell align="center">Pending</TableCell>
              <TableCell align="center">In Progress</TableCell>
              <TableCell align="center">In Review</TableCell>
              <TableCell align="center">Completed</TableCell>
              <TableCell align="center">Accepted</TableCell>
              <TableCell align="center">Rejected</TableCell>
              <TableCell align="center">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {task_metrics.map((board) => (
              <TableRow key={board.board_id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{board.board_name}</Typography>
                </TableCell>
                <TableCell align="center">{board.counts.pending}</TableCell>
                <TableCell align="center">{board.counts.in_progress}</TableCell>
                <TableCell align="center">{board.counts.in_review}</TableCell>
                <TableCell align="center">{board.counts.completed}</TableCell>
                <TableCell align="center">{board.counts.accepted}</TableCell>
                <TableCell align="center">{board.counts.rejected}</TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>{board.counts.total}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {task_metrics.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No boards found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Activity Feed */}
      <Typography variant="h6" sx={{ mb: 2 }}>Recent Activity</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Agent</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recent_activities.map((activity) => (
              <TableRow key={activity.id}>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatTimeAgo(activity.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    @{activity.agent.username}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={EVENT_LABELS[activity.event_type] || activity.event_type}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                    {getActivityDescription(activity)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {recent_activities.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No recent activity.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SidebarLayout>
  )
}
